import ReactDOM from 'react-dom';
import cx from 'classnames';
import { getChordNotes, INVERSION_ORDINALS, splitInversion } from '../../utils/chordNotes';
import { voicedIntervals } from '../../utils/chordVoicing';
import { generateChordPianoKeys } from '../../utils/pianoHelpers';
import useAnchoredPopup from '../../hooks/useAnchoredPopup';
import './ChordPiano.scss';

/**
 * Floating chord keyboard popup.
 *
 * Renders via a portal into document.body so it always escapes any overflow:hidden
 * containers. Positioned above or below the tapped chord element based on available
 * viewport space. Closes when the user taps/clicks anywhere outside.
 *
 * Key highlight states:
 *   chord tone    → primary highlight color
 *   bass note     → bass accent color (purple), isolated to the left with a separator
 *
 * An inverted chord lights the keys it is actually played on rather than the ones it would be in
 * root position, so the shape sits further up the keyboard — which is what turning a chord over
 * does, and the one thing about it worth seeing. The keyboard grows to reach them.
 *
 * A chord from outside the key carries the badge of the scale it is borrowed from, as the Chord
 * Library's legend draws it.
 */
const ChordPiano = ({ chord, anchorRect, onClose, family, familyLabel }) => {
  const popupRef = useAnchoredPopup(anchorRect, onClose);

  const { root, intervals, maxInterval, bassNote, inversion } = getChordNotes(chord);
  const { symbol } = splitInversion(chord);
  const voiced = voicedIntervals(intervals, inversion);
  // Still at least an octave: a first inversion reaches past the root, and the keyboard has to have
  // the key it reaches for before it can light it
  const allKeys = generateChordPianoKeys(root, Math.max(maxInterval, ...voiced), bassNote);

  const bassKey = allKeys.find((k) => k.isBass) ?? null;
  const mainKeys = allKeys.filter((k) => !k.isBass);

  const getKeyRole = (key) => {
    if (key.isBass) return 'bass';
    if (voiced.includes(key.semitoneOffset)) return 'chord-tone';
    return null;
  };

  const renderKey = (key) => {
    const role = getKeyRole(key);
    const showLabel = role !== null; // label only on highlighted keys

    return (
      <div
        key={key.id}
        className={cx('key', {
          'key--white': !key.isBlack,
          'key--black': key.isBlack,
          'key--chord-tone': role === 'chord-tone',
          'key--bass': role === 'bass',
          'key--isolated': key.isBass,
        })}
      >
        {showLabel && <span className="note-label">{key.note}</span>}
      </div>
    );
  };

  const popup = (
    <div
      ref={popupRef}
      className="ChordPiano"
      // Hidden until useLayoutEffect positions it to prevent flicker
      style={{ visibility: 'hidden' }}
    >
      {/* The symbol as the chart draws it, mark and all — see INVERSION_ORDINALS */}
      <div className="header">
        <span className="symbol">
          {symbol}
          {inversion > 0 && <sup>{INVERSION_ORDINALS[inversion]}</sup>}
        </span>
        {familyLabel && <span className={`scale-badge chord-family--${family}`}>{familyLabel}</span>}
      </div>
      <div className="keyboard">
        {bassKey && (
          <>
            <div className="bass-section">
              {renderKey(bassKey)}
              <span className="bass-label">bass</span>
            </div>
            <div className="separator" aria-hidden="true" />
          </>
        )}
        <div className="keys">{mainKeys.map(renderKey)}</div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(popup, document.body);
};

export default ChordPiano;
